import React, { useState } from "react";
import { Loader2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatDateTime } from "@/lib/api";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export const LinkBox = ({ x, onDone }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const show = async () => {
    setOpen(true); setSel([]);
    try { setItems((await api.get(`/fees/phonepe/credits/${x.utr}/candidates`)).data.items); } catch (e) { toast.error(e.response?.data?.detail || "Could not load entries"); }
  };
  const total = (items || []).filter((c) => sel.includes(c.id)).reduce((s, c) => s + Number(c.amount), 0);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/fees/phonepe/credits/${x.utr}/link`, { collection_ids: sel });
      toast.success(data.status === "matched" ? "Linked (already approved)" : "Linked — now approve it in the list above");
      setOpen(false); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Link failed"); } finally { setBusy(false); }
  };
  if (!open) return <Button size="sm" variant="outline" onClick={show} data-testid={`phonepe-link-open-${x.utr}`}>Link to entry…</Button>;
  return (
    <div className="w-full sm:w-96 rounded-lg border border-blue-200 bg-white p-2 text-xs space-y-1" data-testid={`phonepe-link-box-${x.utr}`}>
      <p className="font-semibold text-slate-700">Tick the app entries paid by this {inr(x.amount)} payment</p>
      {!items ? <Loader2 className="h-4 w-4 animate-spin" /> : items.length === 0 ? <p className="text-slate-500">No unmatched UPI entries within 10 days.</p> : items.map((c) => (
        <label key={c.id} className="flex items-start gap-2 cursor-pointer" data-testid={`phonepe-link-item-${c.id}`}>
          <input type="checkbox" checked={sel.includes(c.id)} onChange={(e) => setSel((s) => (e.target.checked ? [...s, c.id] : s.filter((i) => i !== c.id)))} />
          <span>{c.student_name} ({c.roll_number}) · {inr(c.amount)} · {formatDateTime(c.created_at)} · {c.teacher_name} · {c.status}{c.reference ? ` · UTR ${c.reference}` : ""}</span>
        </label>))}
      <div className="flex items-center gap-2 pt-1">
        <span className={total === x.amount ? "text-green-700 font-semibold" : "text-slate-500"} data-testid={`phonepe-link-total-${x.utr}`}>Ticked {inr(total)} of {inr(x.amount)}</span>
        <Button size="sm" disabled={!sel.length || total !== x.amount || busy} onClick={save} className="bg-blue-900 hover:bg-blue-950" data-testid={`phonepe-link-save-${x.utr}`}>Link</Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
};

export const ClearedList = ({ items, onRestore, onRestoreAll, busy }) => (
  <details className="rounded-xl border border-slate-200 bg-white p-4" data-testid="phonepe-cleared">
    <summary className="cursor-pointer font-semibold text-slate-700">Marked "Not a fee" or cleared ({items.length}) — restore if needed</summary>
    <div className="mt-2 space-y-2">
      {items.some((x) => x.status === "not_fee") && (
        <Button size="sm" variant="outline" onClick={onRestoreAll} disabled={busy === "restoreall"} data-testid="phonepe-restore-all"><RotateCcw className="h-4 w-4 mr-1" /> Restore all "Not a fee" and match again</Button>
      )}
      {items.map((x) => (
        <div key={x.utr} className="rounded-lg border border-slate-100 bg-slate-50 p-2 text-sm flex items-center justify-between gap-2" data-testid={`phonepe-cl-${x.utr}`}>
          <div><p className="font-medium text-slate-800">{inr(x.amount)} · {x.name} <span className="text-xs text-slate-400">({x.status === "not_fee" ? "not a fee" : "cleared"})</span></p>
            <p className="text-xs text-slate-500">{formatDateTime(x.at)} · UTR {x.utr}{x.reason ? ` · ${x.reason}` : ""}</p></div>
          <Button size="sm" variant="outline" disabled={busy === x.utr} onClick={() => onRestore(x)} data-testid={`phonepe-restore-${x.utr}`}>Restore</Button>
        </div>))}
    </div>
  </details>
);
